import { prepareSortingParams } from 'api/common-schemas';
import {
  OrdersByLocationAPIResponse,
  CalendarEventsResponse,
  OrdersByLocationSearchParams,
  OrdersByLocationSearchParamsSchema,
  OrdersIndexAPIResponse,
  ResponseActiveOrdersGraphType,
  RecentUpdateItem,
} from './schemas';
import { apiClient } from 'api/apiClient';
import {
  RebillInvoiceDetails,
  RebillLocationsInfo,
} from 'screens/Inventory/NonGRTInvoices/RebillServiceDetailsModal/schemas';
import { LOAModalForm } from 'screens/Inventory/NonGRTInvoices/LOAModal/schemas';
import { buildLOAPayload, buildInvoiceRebillPayload } from './utils';

type CalendarEventsParams =
  | { from_date: string; to_date: string }
  | { month: number; year: number };

export interface IUpdateItem {
  id: string;
  title: string;
  time: string;
  date: string;
  address1: string;
  address2: string;
  subtitle: string;
  tag: {
    label: string;
  };
}

export const getActiveOrdersGraph =
  async (): Promise<ResponseActiveOrdersGraphType> => {
    const response = await apiClient.get('/api/v1/orders/tracking/counts');
    return response.data;
  };

const mapOrderLocationColumns = {
  address_order: 'location',
  percentage_order: 'status',
  site_name_order: 'site_name',
  num_services_order: 'num_services',
};

export const getOrdersByLocation = async (
  args: OrdersByLocationSearchParams,
): Promise<OrdersByLocationAPIResponse> => {
  const newQueryParams = prepareSortingParams(
    args,
    mapOrderLocationColumns,
    OrdersByLocationSearchParamsSchema,
    { sortByKey: 'sort_by', shouldUppercaseOrder: false },
  );

  const response = await apiClient.get('/api/v1/orders/tracking/locations', {
    params: {
      ...newQueryParams,
    },
  });
  return {
    ...response.data,
    metadata: {
      ...response.data.metadata,
      total_items:
        (response.data?.metadata?.total_items ||
          response.data?.metadata?.total) ??
        0,
    },
  };
};

const mapOrderColumns = {
  address_order: 'location',
  g360_status_order: 'status',
  site_name_order: 'site_name',
  order_number_order: 'order_number',
  product_name_order: 'product_name',
  date_ordered_order: 'date_ordered',
  date_confirmed_order: 'date_confirmed',
};

export const getOrdersByOrder = async (
  args: OrdersByLocationSearchParams,
): Promise<OrdersIndexAPIResponse> => {
  const newQueryParams = prepareSortingParams(
    args,
    mapOrderColumns,
    OrdersByLocationSearchParamsSchema,
    { sortByKey: 'sort_by', shouldUppercaseOrder: false },
  );

  const response = await apiClient.get('/api/v1/orders/tracking/orders', {
    params: { ...newQueryParams },
  });
  return {
    ...response.data,
    metadata: {
      ...response.data.metadata,
      total_items:
        (response.data?.metadata?.total_items ||
          response.data?.metadata?.total) ??
        0,
    },
  };
};

export const getRecentUpdates = async (
  params: string[],
): Promise<RecentUpdateItem[]> => {
  const response = await apiClient.get(
    '/api/v1/orders/tracking/recent_updates',
    {
      params: { filter_type: params.join(',') },
    },
  );
  return response.data;
};

export const getExportOrdersAsCVS = async (
  args: Omit<OrdersByLocationSearchParams, 'page' | 'page_size'>,
): Promise<Blob> => {
  const newQueryParams = prepareSortingParams(
    args,
    mapOrderColumns,
    OrdersByLocationSearchParamsSchema,
    { sortByKey: 'sort_by', shouldUppercaseOrder: false },
  );

  const response = await apiClient.get(
    '/api/v1/orders/tracking/orders/download',
    {
      params: { ...newQueryParams },
      responseType: 'blob',
    },
  );
  return response.data;
};

export const getCalendarEvents = async (
  params: CalendarEventsParams,
): Promise<CalendarEventsResponse> => {
  const response = await apiClient.get('/api/v1/orders/tracking/calendar', {
    params,
  });
  return response.data;
};

export const uploadInvoice = async (data: FormData) => {
  const response = await apiClient.post(
    `/api/v1/orders/invoices/upload`,
    data,
    {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    },
  );
  return response.data;
};

export const getInvoiceDetails = async (uploadId: string) => {
  const response = await apiClient.get(`/api/v1/orders/invoices/${uploadId}`);
  return response.data;
};

export const processInvoice = async (uploadId: string) => {
  const response = await apiClient.post(`/api/v1/orders/invoices/process`, {
    upload_id: uploadId,
  });
  return response.data;
};

export const getInvoiceComparison = async (quoteId: string) => {
  const response = await apiClient.get(
    `/api/v1/orders/invoices/compare/${quoteId}`,
  );
  return response.data;
};

export const postInvoiceRebill = (
  invoiceId: string,
  data: RebillLocationsInfo & RebillInvoiceDetails,
) => {
  const payload = buildInvoiceRebillPayload(data);

  return apiClient.post(`/api/v1/orders/invoices/${invoiceId}/rebill`, payload);
};

export const postLOA = async (data: LOAModalForm) => {
  const formData = buildLOAPayload(data);
  const res = await apiClient.post('/api/v1/orders/invoices/loa', formData, {
    responseType: 'blob',
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  const cd = res.headers?.['content-disposition'] as string | undefined;
  const match = cd?.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] ?? 'loa.pdf';

  return { blob: res.data, filename };
};
